import React, { useEffect, useMemo } from 'react';
import { useAppSelector } from 'app/hooks';
import UIBox from 'components/ui/UIBox';
import UIContainer from 'components/ui/UIContainer';
import UIFlex from 'components/ui/UIFlex';
import UIText from 'components/ui/UIText';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import PageIntro from 'components/PageIntro/PageIntro';
import WithLoader from 'components/WithLoader';
import CategoryCardList from 'components/CategoryCardList';
import ResumeCategoryCard from 'components/ResumeCategoryCard';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useParams } from 'react-router-dom';
import commonSlice from 'app/store/common.slice';
import { CategoryTO, WordSetCategoryType } from 'app/types';

interface IWordSetCategoryOverviewProps {}

const pluralizeUnits = (
  wordSetType: WordSetCategoryType | string,
  n: number,
): string => {
  if (wordSetType === 'level-based' || wordSetType === 'irregular-verb') {
    if (n === 1) return 'рівень';
    if (n < 5) return 'рівні';
    return 'рівнів';
  }
  if (n === 1) return 'тема';
  if (n < 5) return 'теми';
  return 'тем';
};

// Mirror of CategoryCardList's parent-accent map — kept inline here so
// the ResumeCategoryCard at the top of the page picks the same colour
// as the cards below it. If a third caller needs this, hoist it.
const WORDSET_PARENT_ACCENT: Record<WordSetCategoryType, string> = {
  'level-based': '#3B2C93',
  'most-common': '#3B2C93',
  'topic-related': '#3B2C93',
  'phrasal-verbs': '#3B2C93',
  collocations: '#3B2C93',
  idioms: '#3B2C93',
  'irregular-verb': '#3B2C93',
};

const pickActiveCategory = (
  categories: CategoryTO[],
): CategoryTO | undefined => {
  let best: CategoryTO | undefined;
  let bestScore = -1;
  categories.forEach((c) => {
    if (c.addedWords <= 0 || c.totalWords <= 0) return;
    if (c.learnedWords >= c.totalWords) return;
    const score = (c.learnedWords / c.totalWords) * 1000 + c.addedWords / c.totalWords;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  });
  return best;
};

const WordSetCategoryOverview: React.FC<IWordSetCategoryOverviewProps> = () => {
  const { wordSetType = '' } = useParams();
  const { data, loading } = useAppSelector((store) =>
    commonSlice
      .getSelectors()
      .getWordSetCategory(store.common, wordSetType as WordSetCategoryType),
  );

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const activeCategory = useMemo(
    () => pickActiveCategory(data?.categories ?? []),
    [data?.categories],
  );

  const count = data?.categories?.length ?? 0;

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>

      <WithLoader loading={loading}>
        <UIContainer sx={{ pb: 8 }}>
          <PageIntro
            title={data?.title || ''}
            description={data?.subTitle}
            aside={
              activeCategory ? (
                <ResumeCategoryCard
                  record={activeCategory}
                  link={fillUrlIds(pagePaths.categoryOverview, {
                    wordSetType: wordSetType as WordSetCategoryType,
                    categoryName: activeCategory.categoryName,
                  })}
                  parentAccent={
                    WORDSET_PARENT_ACCENT[wordSetType as WordSetCategoryType]
                  }
                />
              ) : undefined
            }
          />

          {/* Eyebrow row above the list */}
          {count > 0 && (
            <UIFlex
              ai={'center'}
              sx={{ flexWrap: 'nowrap', gap: 1.5, mb: 1.75 }}
            >
              <UIText
                sx={{
                  color: 'primary.subtitle',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${count} ${pluralizeUnits(wordSetType, count)}`}
              </UIText>
              <UIBox sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
            </UIFlex>
          )}

          {data && (
            <CategoryCardList
              data={data}
              wordSetType={wordSetType as WordSetCategoryType}
              createLink={(record) =>
                fillUrlIds(pagePaths.categoryOverview, {
                  wordSetType: wordSetType as WordSetCategoryType,
                  categoryName: record.categoryName,
                })
              }
            />
          )}
        </UIContainer>
      </WithLoader>
    </>
  );
};

export default WordSetCategoryOverview;
